/** Normalize inventory id from billed line invId (string or populated doc). */
export function normalizeInvId(id) {
  if (id == null || id === false) return null;
  if (typeof id === "object" && id._id !== undefined) return String(id._id);
  return String(id);
}

/** Billing accepts only real inventory lines; SO/PO charge lines have no invId. */
export function filterBillableBdfInventories(inventories) {
  if (!Array.isArray(inventories)) return [];
  return inventories.filter((inv) => !inv?.lineType && inv?.invId);
}

export function findBilledLineById(billedInvs, billingLineId) {
  if (!Array.isArray(billedInvs) || billingLineId == null) return undefined;
  const target = String(billingLineId);
  return billedInvs.find((b) => String(b._id) === target);
}

/** First unused billed line for this inventory (supports duplicate invId on one billing doc). */
export function takeNextBilledLineForInventory(
  billedInvs,
  inventoryId,
  usedLineIds,
) {
  const target = normalizeInvId(inventoryId);
  if (!target || !Array.isArray(billedInvs)) return undefined;

  return billedInvs.find((b) => {
    const lineId = b?._id != null ? String(b._id) : "";
    if (!lineId || usedLineIds.has(lineId)) return false;
    return normalizeInvId(b.invId) === target;
  });
}

/** @deprecated Prefer findBilledLineById when the UI row has a billing line _id. */
export function findBilledLine(billedInvs, inventoryRowId) {
  if (!Array.isArray(billedInvs) || inventoryRowId == null) return undefined;
  const target = normalizeInvId(inventoryRowId);
  if (!target) return undefined;
  return billedInvs.find((b) => normalizeInvId(b.invId) === target);
}

export function findBilledLineForRecord(billedInvs, record) {
  if (!record) return undefined;
  const byLine = findBilledLineById(billedInvs, record._id);
  if (byLine) return byLine;
  return findBilledLine(billedInvs, record.inventoryId ?? record._id);
}

/**
 * Join BDF inventory lines with billing doc lines (unique billing _id per row).
 * BDF order is preserved; each duplicate invId gets the next matching billed line.
 */
/**
 * Subtotal for invoice / e-invoice views: sum billed line amounts + suffix +
 * invoice-level production charges. Optional innovation from BDF lines paired
 * in order with billed lines (duplicate invId safe).
 */
export function computeInvoiceSubtotal({
  billedInvs,
  billedSuffixInvs = [],
  bdfInventories,
  printingCharge = 0,
  mountingCharge = 0,
}) {
  let total = (billedInvs || []).reduce(
    (acc, line) => acc + (Number(line?.amount) || 0),
    0,
  );
  total += (billedSuffixInvs || []).reduce(
    (acc, line) => acc + (Number(line?.amount) || 0),
    0,
  );

  if (Array.isArray(bdfInventories) && Array.isArray(billedInvs)) {
    const usedBilledLineIds = new Set();
    filterBillableBdfInventories(bdfInventories).forEach((bdfInv) => {
      const invId = normalizeInvId(bdfInv?.invId);
      if (!invId) return;
      const billedLine = takeNextBilledLineForInventory(
        billedInvs,
        invId,
        usedBilledLineIds,
      );
      if (!billedLine) return;
      if (billedLine._id != null) {
        usedBilledLineIds.add(String(billedLine._id));
      }
      total += Number(bdfInv?.innovationCharge) || 0;
    });
  }

  total +=
    (Number(printingCharge) || 0) + (Number(mountingCharge) || 0);
  return total;
}

/** Meta rows for invoice table (SRPM, booking period) keyed to each billed line. */
export function buildInvoiceInventoriesFromBilledLines(billedInvs, bdfInventories) {
  const usedBdfKeys = new Set();
  const billableBdfInventories = filterBillableBdfInventories(bdfInventories);

  return (billedInvs || []).map((billedInv, index) => {
    const invId = normalizeInvId(billedInv?.invId);
    const billingLineId =
      billedInv?._id != null
        ? String(billedInv._id)
        : `invoice-billed-${invId}-${index}`;

    const bdfInv = billableBdfInventories.find((inv) => {
      if (normalizeInvId(inv?.invId) !== invId) return false;
      const bdfKey =
        inv?._id != null
          ? String(inv._id)
          : `${invId}-${inv?.startDate ?? ""}-${inv?.endDate ?? ""}`;
      if (usedBdfKeys.has(bdfKey)) return false;
      usedBdfKeys.add(bdfKey);
      return true;
    });

    const invDoc = billedInv?.invId;
    return {
      _id: billingLineId,
      inventoryId: invId,
      locationName: invDoc?.locations?.name || bdfInv?.invId?.locations?.name || null,
      CodeNo: invDoc?.CodeNo || bdfInv?.invId?.CodeNo || null,
      suffix: invDoc?.suffix || bdfInv?.invId?.suffix || "HO",
      isTrading: invDoc?.isTrading || bdfInv?.invId?.isTrading || false,
      ownedBy: invDoc?.ownedBy || bdfInv?.invId?.ownedBy || false,
      mediaTypeId: normalizeInvId(
        invDoc?.typeOfMedia || bdfInv?.invId?.typeOfMedia,
      ),
      rpm: bdfInv?.rpm || null,
      printingCharge: bdfInv?.printingCharge || 0,
      mountingCharge: bdfInv?.mountingCharge || 0,
      specialRate: bdfInv?.specialRate || null,
      specialRatePerMonth: bdfInv?.specialRatePerMonth || 0,
      startDate: bdfInv?.startDate || null,
      endDate: bdfInv?.endDate || null,
    };
  });
}

export function mapInvoiceBilledInvRows(billedInvs) {
  return (billedInvs || []).map((inv, index) => {
    const invId = normalizeInvId(inv?.invId);
    const invDoc = inv?.invId;
    const billingLineId =
      inv?._id != null ? String(inv._id) : `invoice-billed-${invId}-${index}`;

    return {
      _id: billingLineId,
      inventoryId: invId,
      amount: inv?.amount ?? null,
      startDate: inv?.startDate ?? null,
      endDate: inv?.endDate ?? null,
      height: invDoc?.height ?? null,
      width: invDoc?.width ?? null,
      mediaType: invDoc?.Illu ?? null,
      mediaVehical: invDoc?.typeOfMedia?.name ?? null,
      mediaTypeId: normalizeInvId(invDoc?.typeOfMedia),
      locationName: invDoc?.locations?.name ?? null,
      CodeNo: invDoc?.CodeNo ?? null,
      unit: invDoc?.unit ?? null,
      isTrading: invDoc?.isTrading ?? false,
      ownedBy: invDoc?.ownedBy ?? null,
    };
  });
}

export function buildBillingInventoryRows(bdfInventories, billedInvs) {
  if (!Array.isArray(bdfInventories)) return [];

  const usedBillingLineIds = new Set();

  // Agency Commission / Transportation are SO/PO charge lines, not billable
  // inventory rows. Billing only handles records with a real inventory id.
  return filterBillableBdfInventories(bdfInventories)
    .map((inv, index) => {
      const inventoryId =
        normalizeInvId(inv?.invId) || normalizeInvId(inv?._id) || false;
      const billedLine = takeNextBilledLineForInventory(
        billedInvs,
        inventoryId,
        usedBillingLineIds,
      );

      if (billedLine?._id != null) {
        usedBillingLineIds.add(String(billedLine._id));
      }

      const billingLineId = billedLine?._id
        ? String(billedLine._id)
        : `billing-row-${inventoryId}-${index}`;

      const rawDue = billedLine?.dueAmount;
      const dueNumber =
        rawDue !== undefined && rawDue !== null && rawDue !== ""
          ? Number(rawDue)
          : null;

      return {
        _id: billingLineId,
        inventoryId,
        locationName: inv?.invId?.locations?.name || null,
        CodeNo: inv?.invId?.CodeNo || null,
        suffix: inv?.invId?.suffix || "HO",
        isTrading: inv?.invId?.isTrading || false,
        ownedBy: inv?.invId?.ownedBy || false,
        mediaTypeId: normalizeInvId(inv?.invId?.typeOfMedia),
        rpm: inv?.rpm || null,
        printingCharge: inv?.printingCharge || 0,
        mountingCharge: inv?.mountingCharge || 0,
        specialRate:
          dueNumber !== null && Number.isFinite(dueNumber)
            ? dueNumber
            : inv?.specialRate || null,
        originalSpecialRate: inv?.specialRate || null,
        specialRatePerMonth: inv?.specialRatePerMonth || 0,
        startDate: inv?.startDate || null,
        endDate: inv?.endDate || null,
        apiDueAmount: billedLine?.dueAmount,
        billedPeriod: billedLine?.billedPeriod || [],
      };
    });
}
